<script setup lang="ts">
import { computed } from 'vue';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import { InputGroup, Typography } from '@apparatix/library/core';
import DetailEditorBase from './DetailEditorBase.vue';
import type { AddressSchema, WebPageSchema, PhoneNumberSchema } from '@apparatix/schemas/Addressable';
import type { OfficeSchema } from '@apparatix/schemas/Office';
import type { OwnerSchema } from '@apparatix/schemas/Owner';
import type {
  AccountDetailErrors, IndustrySchema, LabelSchema, MarketSchema, SourceSchema,
} from '@apparatix/schemas/AccountDetail';
import { useAccountStore } from '@apparatix/stores/useAccountStore.ts';
import { PhoneNumber } from '@apparatix/types/Account.ts';

type Props = {
  noHeadingLabels?: boolean;
  errors: AccountDetailErrors;
  canChangeOwner?: boolean;
}

const props = defineProps<Props>();
const store = useAccountStore();

const leftColLabel = computed(() => {
  if (props.noHeadingLabels) return undefined;
  return 'General';
});

const rightColLabel = computed(() => 'Get in Touch');

const owner = defineModel<OwnerSchema>('owner');
const name = defineModel<string>('name');
const legalName = defineModel<string>('legalName');
const industry = defineModel<IndustrySchema>('industry');
const markets = defineModel<MarketSchema[]>('markets');
const label = defineModel<LabelSchema | null>('label');
const office = defineModel<OfficeSchema>('office');
const source = defineModel<SourceSchema>('source');
const phoneNumbers = defineModel<PhoneNumberSchema[]>('phoneNumbers');
const webPages = defineModel<WebPageSchema[]>('webPages');
const addresses = defineModel<AddressSchema[]>('addresses');

const getPhoneNumber = (type: string) => phoneNumbers.value.find(p => p.addressType.toLowerCase() === type.toLowerCase())?.number || '';

const setPhoneNumber = (type: string, newValue: string) => {
  const index = phoneNumbers.value.findIndex((p: PhoneNumber) => p.addressType.toLowerCase() === type.toLowerCase());

  if (index !== -1) {
    // Edit the property of the existing object in the array
    phoneNumbers.value[index].number = newValue;
  } else {
    // If it doesn't exist, add a new object
    phoneNumbers.value.push({ number: newValue, addressType: type });
  }
};

const workPhone = computed({
  get: () => getPhoneNumber('Work'),
  set: (newValue: string) => setPhoneNumber('Work', newValue),
});

const cellPhone = computed({
  get: () => getPhoneNumber('Cell'),
  set: (newValue: string) => setPhoneNumber('Cell', newValue),
});

const faxPhone = computed({
  get: () => getPhoneNumber('Fax'),
  set: (newValue: string) => setPhoneNumber('Fax', newValue),
});

const webPage = computed({
  get: () => webPages.value[0]?.url || '',
  set: (newValue: string) => {
    const existingWebPage = webPages.value[0];
    if (existingWebPage) {
      existingWebPage.url = newValue;
    } else {
      webPages.value.push({ url: newValue, addressType: 'Work' });
    }
  },
});
</script>
<template>
  <DetailEditorBase :left-col-header="leftColLabel" :right-col-header="rightColLabel">
    <template #left-col>
      <InputGroup>
        <Typography variant="form-label">
          Owner
        </Typography>
        <Select
          v-model="owner"
          :invalid="Boolean(props.errors?.owner)"
          :options="store.options.users"
          option-label="fullName"
          :disabled="store.accountOwnerLocked"
          :placeholder="owner.fullName"
          fluid
        />
        <Typography v-if="Boolean(props.errors?.owner)" variant="form-error">
          {{ props.errors.owner }}
        </Typography>
        <Typography variant="form-label">
          Name
        </Typography>
        <InputText
          v-model="name"
          :disabled="store.invoiceFieldsLocked"
          :invalid="Boolean(props.errors?.name)"
          fluid
        />
        <Typography v-if="Boolean(props.errors?.name)" variant="form-error">
          {{ props.errors.name }}
        </Typography>
        <Typography variant="form-label">
          Legal Name
        </Typography>
        <InputText
          v-model="legalName"
          :disabled="store.invoiceFieldsLocked"
          :invalid="Boolean(props.errors?.legalName)"
          fluid
        />
        <Typography v-if="Boolean(props.errors?.legalName)" variant="form-error">
          {{ props.errors.legalName }}
        </Typography>
        <Typography variant="form-label">
          Account Source
        </Typography>
        <Select
          v-model="source"
          :invalid="Boolean(props.errors?.source)"
          :options="store.options.sources"
          option-label="selection"
          :placeholder="source?.selection"
          fluid
        />
        <Typography v-if="Boolean(props.errors?.source)" variant="form-error">
          {{ props.errors.source }}
        </Typography>
        <Typography variant="form-label">
          Industry
        </Typography>
        <Select
          v-model="industry"
          :invalid="Boolean(props.errors?.industry)"
          :options="store.options.sicCodes"
          option-label="description"
          filter
          filter-placeholder="Search industries"
          :placeholder="industry?.description"
          fluid
        />
        <Typography v-if="Boolean(props.errors?.industry)" variant="form-error">
          {{ props.errors.industry }}
        </Typography>
        <Typography variant="form-label">
          Markets
        </Typography>
        <MultiSelect
          v-model="markets"
          :options="store.options.markets"
          option-label="market"
        />
        <Typography v-if="Boolean(props.errors?.markets)" variant="form-error">
          {{ props.errors.markets }}
        </Typography>
        <Typography variant="form-label">
          Label
        </Typography>
        <Select
          v-model="label"
          :options="store.options.labels"
          option-label="value"
          placeholder="Select Label"
          show-clear
          fluid
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex items-center gap-2">
              <span
                class="h-2.5 w-2.5 rounded-full inline-block"
                :style="{ backgroundColor: slotProps.value.color }"
              />
              <span>{{ slotProps.value.value }}</span>
            </div>
            <span v-else class="text-[var(--p-text-muted-color)]">
              Select Label
            </span>
          </template>
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <span
                class="h-2.5 w-2.5 rounded-full inline-block"
                :style="{ backgroundColor: slotProps.option.color }"
              />
              <span>{{ slotProps.option.value }}</span>
            </div>
          </template>
        </Select>
        <template v-if="store.options.authorizedOffices.length > 1">
          <Typography variant="form-label">
            Office
          </Typography>
          <Select
            v-model="office"
            :invalid="Boolean(props.errors?.office)"
            :options="store.options.authorizedOffices"
            option-label="office"
            placeholder="Select Office"
            fluid
          />
          <Typography v-if="Boolean(props.errors?.office)" variant="form-error">
            {{ props.errors.office }}
          </Typography>
        </template>
      </InputGroup>
    </template>
    <template #right-col>
      <InputGroup>
        <Typography variant="form-label">
          Phone
        </Typography>
        <InputText v-model="workPhone" fluid />
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Cell
        </Typography>
        <InputText v-model="cellPhone" fluid />
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Fax
        </Typography>
        <InputText v-model="faxPhone" fluid />
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Website
        </Typography>
        <InputText v-model="webPage" fluid />
      </InputGroup>
      <div class="flex items-center w-full pb-3 lg:pb-4 border-b border-[var(--p-content-border-color)]">
        <Typography variant="form-heading">
          Location
        </Typography>
      </div>
      <InputGroup>
        <Typography variant="form-label">
          Address
        </Typography>
        <InputText v-model="addresses[0].addressLine1" placeholder="Address Line 1" fluid />
        <InputText v-model="addresses[0].addressLine2" placeholder="Address Line 2" fluid />
        <InputText v-model="addresses[0].city" placeholder="City" fluid />
        <InputText v-model="addresses[0].region" placeholder="State" fluid />
        <InputText v-model="addresses[0].postalCode" placeholder="Postal Code" fluid />
      </InputGroup>
    </template>
  </DetailEditorBase>
</template>
